<template>
<div>
  <label for="openSidebarMenu" class="sidebarIconToggle">
    <div class="spinner diagonal part-1"></div>
    <div class="spinner horizontal"></div>
    <div class="spinner diagonal part-2"></div>
  </label>
  <div id="sidebarMenu" :class="helpMenuOpened ? 'opened':''">
    <ul class="sidebarMenuInner">
      <HelpSectionAddon />
      <li>
        <ul>
          <li><a href="#" v-on:click.prevent="openModalByRef('modal-howto-search')">Perform a search</a></li>
          <li v-if="policyRules"><a href="#" v-on:click.prevent="openModalByRef('modal-data-policy-rules')">Data policy rules</a></li>
          <li v-if="privateFiles" class="private-data-help"><a href="#" v-on:click.prevent="openModalByRef('modal-howto-private-data')">Access private data</a></li>
          <li><a href="#" v-on:click.prevent="openModalByRef('modal-howto-download-tar')">Download multiple files generating a tar archive</a></li>
          <li><a href="#" v-on:click.prevent="openModalByRef('modal-external-download')">Download multiple files using an external client (command line or desktop application)</a></li>
          <li><a href="#" v-on:click.prevent="openModalByRef('modal-samp')">Sending data using SAMP</a></li>
          <li v-if="HelpSectionExtra01"><a href="#" v-on:click.prevent="openModalByRef('modal-help-section-extra-01')"><HelpSectionExtra01 /></a></li>
        </ul>
      </li>
    </ul>
  </div>
  <b-modal size="lg" ref="modal-howto-search" title="Perform a search" ok-only>
    <h5>Form fields</h5>
    <p>The input fields can be used to filter your searches, while the checkboxes near the fields are used to define the visibility of the columns in the search result table (if a checkbox is selected the related column appears in the output table).
    </p>
    <h5 v-if="nameResolver">Name resolver</h5>
    <p v-if="nameResolver">The name resolver input can be used to convert the name of an astronomical object (e.g. M31) into coordinates. Internally the portal calls the <a href="http://cds.u-strasbg.fr/cgi-bin/Sesame" target="_blank">CDS Sesame
        Name Resolver</a>.</p>
    <h5 class="date-range-help">Date range</h5>
    <p class="date-range-help" v-if="singleNight">Instead of specifying a datetime range you can select the "single night" checkbox and specify just a date. In this case the time goes from noon to noon of the day after.</p>
    <p class="date-range-help" v-if="!singleNight">If you specify a datetime range consider that the time is set to 00:00, so to see all the files of one night you have to specify a range going from one day to the day after.</p>
  </b-modal>
  <b-modal size="lg" ref="modal-data-policy-rules" title="Data policy rules" ok-only v-if="policyRules">
    <PolicyRules />
  </b-modal>
  <b-modal size="lg" ref="modal-help-section-extra-01" ok-only v-if="HelpSectionExtra01">
    <HelpSectionExtra01Modal />
  </b-modal>
  <b-modal size="lg" ref="modal-howto-private-data" title="Access private data" ok-only>
    <PrivateData />
    <p>For the login the portal relies on a separate application called RAP (Remote Authentication Portal), which support multiple authentication methods.</p>
    <p>Authorization is managed using another tool called <a href="https://sso.ia2.inaf.it/gms" target="_blank">GMS (Group Membership Service)</a>. PIs can autonomously authorize collaborators accessing the data using this tool.</p>
    <p>See also:
      <ul>
        <li><a href="https://sso.ia2.inaf.it/home/" target="_blank">Information about login</a></li>
        <li><a href="https://sso.ia2.inaf.it/gms/help/index.html" target="_blank">GMS help page</a></li>
        <li><a href="https://sso.ia2.inaf.it/home/faq.php?lang=en" target="_blank">Login FAQ</a></li>
      </ul>
    </p>
  </b-modal>
  <b-modal size="lg" ref="modal-external-download" title="Downloading files with external clients" ok-only>
    <p>First of all generate the list of files clicking on the "URL list" item of the "Download" menu:</p>
    <div class="row">
      <div class="col text-center mb-4">
        <img src="@/assets/images/portal-download-url-list.jpg" />
      </div>
    </div>
    <p>After your URLs list has been generated you can download it from the "Your files" menu (top menu, on the right),
      then you can use it for retrieving all the files using a non-browser client.</p>
    <h4 v-if="privateFiles">Public files</h4>
    <h5>wget</h5>
    <p><code>wget -i files-list.txt</code></p>
    <div v-if="privateFiles">
      <h4>Private files</h4>
      <p>Download the access token clicking on the "Command line access token" item of the "Download" menu:</p>
      <div class="row">
        <div class="col text-center mb-4">
          <img src="@/assets/images/portal-download-token.jpg" />
        </div>
      </div>
      <p>This temporary access token is valid for 24 hours. You have to pass it to your command line client:</p>
      <h5>wget</h5>
      <p><code>wget --header="Authorization: Bearer $(cat token.txt)" -i files-list.txt</code></p>
      <p><strong>Warning</strong>: this command works in bash. If you are using a different shell (like csh) it could be necessary to replace <code>$(cat token.txt)</code> with the token content.</p>
    </div>
  </b-modal>
  <b-modal size="lg" ref="modal-howto-download-tar" title="Download multiple files generating a tar archive" ok-only>
    <div class="row">
      <div class="col">
        <img src="@/assets/images/portal-files-selection.jpg" />
      </div>
      <div class="col">
        <p>You can download multiple files as tar archives.</p>
        <p>Select your files and press the "Create tar from selected" button from the "Download" menu.</p>
        <p>You can find your files in the "Your files" menu (top menu, on the right).</p>
        <p>If you tar is bigger than 500MB it will be split in multiple files.</p>
        <p>If you are going to download a huge amount of files, please consider <a href="#" v-on:click="openModalByRef('modal-external-download')">using an external client like wget for downloading them</a>.</p>
      </div>
    </div>
    <div class="row mt-4">
      <div class="col">
        <img src="@/assets/images/files-done.jpg" />
      </div>
      <div class="col">
        <img src="@/assets/images/files-menu.jpg" />
      </div>
    </div>
  </b-modal>
  <b-modal size="lg" ref="modal-samp" title="Sending data using SAMP" ok-only>
    <p><a href="http://www.ivoa.net/documents/SAMP/index.html" target="_blank">SAMP</a> is a messaging protocol that enables astronomy software tools to interoperate and communicate.</p>
    <p>Using SAMP you can send images or their metadata to external application like <a href="http://www.star.bris.ac.uk/~mbt/topcat/" target="_blank">TOPCAT</a>, <a href="http://aladin.u-strasbg.fr/" target="_blank">Aladin</a>, <a
        href="http://ds9.si.edu/" target="_blank">DS9</a> and many more.</p>
    <div class="row mb-3">
      <div class="col">
        <img src="@/assets/images/portal-no-samp.jpg" />
      </div>
      <div class="col">
        <p>If no SAMP hub is running on your computer this icon is gray.</p>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col">
        <img src="@/assets/images/portal-samp-register.jpg" />
      </div>
      <div class="col">
        <p>If your SAMP hub is running the icon is green and you can register on it to see the other applications.</p>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col">
        <img src="@/assets/images/portal-samp-list.jpg" />
      </div>
      <div class="col">
        <p>When you are registered to the hub you can select the target application.</p>
        <p>If you are unregistered or you haven't select any application your messages will be sent to all active applications.</p>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <img src="@/assets/images/portal-samp-votable.jpg" /><br />
        <img src="@/assets/images/portal-samp-single-file.jpg" class="mt-3" /><br />
      </div>
      <div class="col">
        <p>You can:
          <ul>
            <li>Send a VOTable by SAMP (MType <code>table.load.votable</code>)</li>
            <li>Send an image by SAMP (MType <code>image.load.fits</code>)</li>
          </ul>
        </p>
        <p>When a SAMP hub is detected, a "signal" icon will appear near search result files and generated VOTable files.</p>
      </div>
    </div>
  </b-modal>
</div>
</template>

<script>
import { mapState } from 'vuex';
import PolicyRules from 'PolicyRules';
import HelpSectionAddon from 'HelpSectionAddon';
import HelpSectionExtra01 from 'HelpSectionExtra01';
import HelpSectionExtra01Modal from 'HelpSectionExtra01Modal';
import PrivateData from 'PrivateData';

export default {
  name: 'HelpSidebar',
  computed: mapState({
    helpMenuOpened: state => state.helpMenuOpened
  }),
  components: {
    PolicyRules,
    HelpSectionAddon,
    HelpSectionExtra01,
    HelpSectionExtra01Modal,
    PrivateData
  },
  data() {
    return {
      privateFiles: process.env.VUE_APP_LOGIN === 'true',
      nameResolver: process.env.VUE_APP_NAME_RESOLVER === 'true',
      singleNight: process.env.VUE_APP_SINGLE_NIGHT === 'true',
      policyRules: process.env.HAS_POLICY_RULES === 'true',
      HelpSectionExtra01: process.env.HAS_HELP_SECTION_EXTRA_01 === 'true'
    };
  },
  created() {
    // add global reference to this component, so its methods can be used by other components
    this.$root.$refs.helpSidebar = this;
  },
  mounted() {
    document.addEventListener('click', this.closeSidebarMenuClickingOutside);
  },
  destroyed() {
    document.removeEventListener('click', this.closeSidebarMenuClickingOutside);
  },
  methods: {
    openModalByRef(ref) {
      this.hideOpenedModals();
      this.$refs[ref].show();
    },
    hideOpenedModals() {
      Object.values(this.$refs).forEach(r => {
        // TODO: find a way to check that this object is a modal
        if (typeof r.hide === 'function') {
          r.hide();
        }
      });
    },
    closeSidebarMenuClickingOutside(event) {
      if (!this.helpMenuOpened) {
        return;
      }
      if (isClickOnModal(event.target)) {
        // skip modal close event
        return;
      }
      let sidebarMenu = document.getElementById('sidebarMenu');
      if (event.clientX > sidebarMenu.getBoundingClientRect().width) {
        document.querySelector('.sidebarIconToggle').click();
      }
    }
  }
}

function isClickOnModal(node) {
  let css = node.getAttribute('class');
  if (css !== null && css.includes('fade')) { // modal fade
    return true;
  }
  while (node !== null && node.getAttribute) {
    css = node.getAttribute('class');
    if (css !== null && css.includes('modal-dialog')) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
}
</script>

<style>
#sidebarMenu {
  height: 100%;
  position: fixed;
  top: 56px;
  left: 0;
  width: 300px;
  transform: translateX(-300px);
  transition: transform 250ms ease-in-out;
  background-color: #fff;
  border-right: 1px #dfdfdf solid;
  padding-top: 10px;
  z-index: 500;
}

.sidebarMenuInner {
  margin: 0;
  padding: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.10);
}

.sidebarMenuInner li {
  list-style: none;
  color: #222;
  padding: 3px;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.10);
}

.sidebarMenuInner>li {
  font-weight: bold;
  padding-left: 10px;
}

.sidebarMenuInner>li>ul>li {
  font-weight: normal !important;
  padding-left: 0px;
  list-style: circle;
}

.sidebarMenuInner li span {
  display: block;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.50);
}

.sidebarMenuInner li a {
  cursor: pointer;
}

.sidebarIconToggle {
  transition: all 0.3s;
  box-sizing: border-box;
  cursor: pointer;
  position: absolute;
  z-index: 99;
  height: 100%;
  width: 100%;
  top: 19px;
  left: 15px;
  height: 22px;
  width: 22px;
  padding: 4px 0;
}

.sidebarIconToggle div {
  background-color: #555;
}

.sidebarSpinner {
  transition: all 0.3s;
  box-sizing: border-box;
  position: absolute;
  height: 3px;
  width: 100%;
  background-color: #fff;
}

.horizontal {
  transition: all 0.3s;
  box-sizing: border-box;
  position: relative;
  float: left;
  margin-top: 3px;
}

.diagonal.part-1 {
  position: relative;
  transition: all 0.3s;
  box-sizing: border-box;
  float: left;
}

.diagonal.part-2 {
  transition: all 0.3s;
  box-sizing: border-box;
  position: relative;
  float: left;
  margin-top: 3px;
}

#sidebarMenu.opened {
  transform: translateX(0);
}

#openSidebarMenu {
  transition: all 0.3s;
  box-sizing: border-box;
  display: none;
}

#openSidebarMenu:checked~.sidebarIconToggle>.horizontal {
  transition: all 0.3s;
  box-sizing: border-box;
  opacity: 0;
}

#openSidebarMenu:checked~.sidebarIconToggle>.diagonal.part-1 {
  transition: all 0.3s;
  box-sizing: border-box;
  transform: rotate(135deg);
  margin-top: 8px;
}

#openSidebarMenu:checked~.sidebarIconToggle>.diagonal.part-2 {
  transition: all 0.3s;
  box-sizing: border-box;
  transform: rotate(-135deg);
  margin-top: -9px;
}

#help-label {
  margin-left: 30px;
  margin-top: 4px;
}
</style>
